import { useState } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Building2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline "Add new Prospect account" form. Used inside both Add-Call and
 * Schedule-Call modals so a rep can spin up a brand-new customer + primary
 * contact without leaving the flow. On success calls `onCreated({ account, contact })`
 * with shapes matching the existing account/contact pickers.
 */
export const AddProspectAccountInline = ({ onCreated, onCancel, themeColor = 'orange', initialPhone = '', initialAccountName = '' }) => {
  const [accountName, setAccountName] = useState(initialAccountName);
  const [accountPhone, setAccountPhone] = useState(initialPhone);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [contactPhone, setContactPhone] = useState(initialPhone);
  const [saving, setSaving] = useState(false);

  const ringClass = themeColor === 'purple' ? 'focus:ring-purple-200' : 'focus:ring-orange-200';
  const inputClass = `rounded-xl border-gray-200 bg-gray-50/60 focus:bg-white focus:ring-2 ${ringClass} h-11`;
  const saveButtonColor = themeColor === 'purple' ? '#6d28d9' : '#ee7203';

  const handleSave = async () => {
    const an = accountName.trim();
    const ap = accountPhone.trim();
    const ln = lastName.trim();
    if (!an) return toast.error('Account name is required');
    if (!ap) return toast.error('A contact number for the account is required');
    if (!ln) return toast.error('Contact last name is required');
    setSaving(true);
    try {
      const res = await api.post(`${API}/zoho/create-prospect-account`, {
        account_name: an,
        account_phone: ap,
        contact_first_name: firstName.trim() || null,
        contact_last_name: ln,
        contact_email: email.trim() || null,
        contact_mobile: contactPhone.trim() || null,
      });
      toast.success('Prospect account created');
      onCreated?.({
        account: res.data.account,
        contact: res.data.contact,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`mt-2 rounded-xl border ${themeColor === 'purple' ? 'border-purple-200 bg-purple-50/40' : 'border-orange-200 bg-orange-50/40'} p-3 space-y-2`}
      data-testid="prospect-account-form"
    >
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-600">
        <Building2 className="w-3.5 h-3.5" />
        New prospect account + primary contact
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input
          value={accountName}
          onChange={(e) => setAccountName(e.target.value)}
          placeholder="Account / company name *"
          className={`sm:col-span-2 ${inputClass}`}
          data-testid="prospect-account-name"
        />
        <Input
          value={accountPhone}
          onChange={(e) => setAccountPhone(e.target.value)}
          placeholder="Contact number for this account *"
          className={`sm:col-span-2 ${inputClass}`}
          data-testid="prospect-account-phone"
        />
        <div className="sm:col-span-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-600 pt-1">
          <UserPlus className="w-3.5 h-3.5" /> Primary contact
        </div>
        <Input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name"
          className={inputClass}
          data-testid="prospect-contact-first"
        />
        <Input
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Last name *"
          className={inputClass}
          data-testid="prospect-contact-last"
        />
        <Input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Contact email (optional)"
          type="email"
          className={inputClass}
          data-testid="prospect-contact-email"
        />
        <Input
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
          placeholder="Contact number (optional)"
          type="tel"
          className={inputClass}
          data-testid="prospect-contact-phone"
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="rounded-xl h-9 px-3 text-xs"
          data-testid="prospect-cancel"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-xl h-9 px-3 text-xs text-white font-semibold"
          style={{ backgroundColor: saveButtonColor }}
          data-testid="prospect-save"
        >
          {saving ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Creating…</> : 'Create account'}
        </Button>
      </div>
    </div>
  );
};

export default AddProspectAccountInline;
